"use client";

import React, { useEffect, useRef } from "react";
import AppImage from "@/components/ui/AppImage";

export default function ExperienceSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) e.target.classList.add("is-visible");
        });
      },
      { threshold: 0 }
    );
    sectionRef?.current?.querySelectorAll(".reveal-hidden")?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <section
      id="ingredients"
      ref={sectionRef}
      className="section-pad bg-pmu-cream relative overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-2 gap-12 lg:gap-20 items-center">

          {/* Left: Copy */}
          <div className="space-y-8">
            <div className="reveal-hidden">
              <p className="text-xs font-medium uppercase tracking-widest text-pmu-taupe mb-6" style={{ letterSpacing: "0.2em" }}>
                The Experience
              </p>
              <h2
                className="font-fraunces font-bold text-pmu-espresso leading-tight"
                style={{ fontSize: "clamp(2rem, 3.5vw, 2.8rem)" }}
              >
                Comfort &amp; nutrition{" "}
                <span className="italic font-light" style={{ color: "#A8C4D8" }}>in every bite</span>
              </h2>
            </div>

            <div className="reveal-hidden delay-1">
              <p className="text-base text-pmu-taupe leading-relaxed" style={{ fontWeight: 400 }}>
                Imagine the soft, chewy texture of oats combined with the nutty richness
                of chickpeas and a hint of natural sweetness. It&apos;s the perfect blend of
                comfort and nutrition — making every bite a small victory for your body
                and taste buds.
              </p>
            </div>

            {/* Sensory details — clean list */}
            <div className="reveal-hidden delay-2 space-y-5">
              {[
                {
                  title: "Chewy Oat Base",
                  desc: "Rolled oats give every cookie that satisfying, hearty chew you crave.",
                },
                {
                  title: "Nutty Chickpea Richness",
                  desc: "Chickpeas add depth of flavor and a protein punch you won't find anywhere else.",
                },
                {
                  title: "Sustained Energy Fuel",
                  desc: "Balanced ingredients designed to deliver steady energy—no crashes, just lasting fuel for your day.",
                },
              ]?.map(({ title, desc }, i) => (
                <div key={title} className={`flex gap-5 items-start reveal-hidden delay-${i + 2}`}>
                  <div
                    className="w-1 h-12 rounded-full shrink-0 mt-0.5"
                    style={{ background: "#A8C4D8" }}
                  />
                  <div>
                    <p className="font-medium text-pmu-espresso mb-1">{title}</p>
                    <p className="text-sm text-pmu-taupe leading-relaxed" style={{ fontWeight: 400 }}>{desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Quote */}
            <div
              className="reveal-hidden delay-5 p-6 rounded-2xl"
              style={{ background: "#FFFFFF", border: "1px solid rgba(214, 207, 196, 0.5)" }}
            >
              <p className="font-fraunces text-base font-light text-pmu-espresso italic leading-relaxed">
                &ldquo;Our cookie isn&apos;t just food; it&apos;s a hug in every bite. Crafted for
                health-conscious consumers, fitness enthusiasts, and cookie lovers alike.&rdquo;
              </p>
            </div>
          </div>

          {/* Right: Image */}
          <div className="reveal-hidden delay-1 relative">
            <div className="relative rounded-3xl shadow-lift flex items-center justify-center" style={{ minHeight: "320px" }}>
              <AppImage
                src="/assets/images/cookie_side_angle.png"
                alt="Close-up side angle of a thick chunky oat and chickpea chocolate chip cookie showing its height and rustic texture"
                width={560}
                height={420}
                priority
                className="object-contain w-full h-auto max-h-[500px] rounded-3xl"
                sizes="(max-width: 1024px) 100vw, 50vw"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}